import { useState, useEffect, useRef, useId } from 'react'
import { useTheme } from '@/store/useTheme'
import { PALETTES } from '@/store/palettes'
import { useLogout } from '@/hooks/useLogout'
import {
  loadBackdropBlob,
  loadPageBackdropBlob,
  saveBackdropBlob,
  savePageBackdropBlob,
  clearBackdropBlob,
  clearPageBackdropBlob,
  resizeImage,
} from '@/utils/clientBackdrop'
import styles from './YourSpacePage.module.css'

const NOTE_STORAGE_KEY = 'fitutogether-your-space-note'
const CUSTOM_COLOR_KEY = 'fitutogether-your-space-custom-color'
const USE_CUSTOM_COLOR_KEY = 'fitutogether-your-space-use-custom-color'
const SOFTEN_BG_KEY = 'fitutogether-your-space-soften-bg'

function readStorage(key: string, fallback = ''): string {
  try { return localStorage.getItem(key) ?? fallback }
  catch { return fallback }
}

function writeStorage(key: string, value: string): void {
  try { localStorage.setItem(key, value) }
  catch { /* quota — fail silently */ }
}

// ── Icons ───────────────────────────────────────────────────────────────

function CameraIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8
               a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  )
}

function TickIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none"
      stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="2 6 5 9 10 3" />
    </svg>
  )
}

function LandscapeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 15l5-5 4 4 3-3 6 6" />
      <circle cx="8.5" cy="10" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  )
}

// ── Page ────────────────────────────────────────────────────────────────

export default function YourSpacePage() {
  const { palette, setPalette } = useTheme()
  const logout = useLogout()
  const textareaId      = useId()
  const headerUploadId  = useId()
  const pageUploadId    = useId()
  const customColorId   = useId()
  const softenToggleId  = useId()
  const headerFileRef   = useRef<HTMLInputElement>(null)
  const pageFileRef     = useRef<HTMLInputElement>(null)
  const headerUrlRef    = useRef<string | null>(null)
  const pageUrlRef      = useRef<string | null>(null)

  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(null)
  const [pageBackdropUrl, setPageBackdropUrl] = useState<string | null>(null)

  const [note, setNote] = useState<string>(() => readStorage(NOTE_STORAGE_KEY))

  const [customColor, setCustomColor] = useState(
    () => readStorage(CUSTOM_COLOR_KEY, '#FDFCFA'),
  )
  const [useCustomColor, setUseCustomColor] = useState(
    () => readStorage(USE_CUSTOM_COLOR_KEY) === 'true',
  )
  const [softenBackground, setSoftenBackground] = useState(
    () => readStorage(SOFTEN_BG_KEY) === 'true',
  )

  const revokeHeaderUrl = () => {
    if (headerUrlRef.current) {
      URL.revokeObjectURL(headerUrlRef.current)
      headerUrlRef.current = null
    }
  }

  const revokePageUrl = () => {
    if (pageUrlRef.current) {
      URL.revokeObjectURL(pageUrlRef.current)
      pageUrlRef.current = null
    }
  }

  const clearHeaderPhoto = async () => {
    revokeHeaderUrl()
    setHeaderImageUrl(null)
    await clearBackdropBlob()
  }

  const clearPageBackdrop = async () => {
    revokePageUrl()
    setPageBackdropUrl(null)
    await clearPageBackdropBlob()
  }

  useEffect(() => {
    let active = true

    Promise.all([loadBackdropBlob(), loadPageBackdropBlob()])
      .then(async ([headerBlob, pageBlob]) => {
        if (!active) return

        if (pageBlob) {
          if (headerBlob) await clearBackdropBlob()
          const url = URL.createObjectURL(pageBlob)
          pageUrlRef.current = url
          setPageBackdropUrl(url)
          return
        }

        if (headerBlob) {
          const url = URL.createObjectURL(headerBlob)
          headerUrlRef.current = url
          setHeaderImageUrl(url)
        }
      })
      .catch(() => { /* IDB unavailable — degrade gracefully */ })

    return () => {
      active = false
      revokeHeaderUrl()
      revokePageUrl()
    }
  }, [])

  const handleHeaderFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    try {
      await clearPageBackdrop()
      const resized = await resizeImage(file)
      revokeHeaderUrl()
      const url = URL.createObjectURL(resized)
      headerUrlRef.current = url
      setHeaderImageUrl(url)
      await saveBackdropBlob(resized)
    } catch (err) {
      console.warn('[YourSpace] header image resize/save failed:', err)
    }
  }

  const handlePageBackdropFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    try {
      await clearHeaderPhoto()
      const resized = await resizeImage(file)
      revokePageUrl()
      const url = URL.createObjectURL(resized)
      pageUrlRef.current = url
      setPageBackdropUrl(url)
      await savePageBackdropBlob(resized)
    } catch (err) {
      console.warn('[YourSpace] page backdrop resize/save failed:', err)
    }
  }

  const handleRemovePhoto = async () => {
    try {
      if (pageBackdropUrl) {
        await clearPageBackdrop()
      } else if (headerImageUrl) {
        await clearHeaderPhoto()
      }
    } catch (err) {
      console.warn('[YourSpace] photo remove failed:', err)
    }
  }

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value)
    writeStorage(NOTE_STORAGE_KEY, e.target.value)
  }

  const handlePaletteSelect = (id: string) => {
    setUseCustomColor(false)
    writeStorage(USE_CUSTOM_COLOR_KEY, 'false')
    setPalette(id)
  }

  const handleCustomColor = (hex: string) => {
    setCustomColor(hex)
    setUseCustomColor(true)
    writeStorage(CUSTOM_COLOR_KEY, hex)
    writeStorage(USE_CUSTOM_COLOR_KEY, 'true')
  }

  const handleSoftenToggle = (checked: boolean) => {
    setSoftenBackground(checked)
    writeStorage(SOFTEN_BG_KEY, String(checked))
  }

  const activeBg = useCustomColor ? customColor : palette.bg
  const hasPhoto = Boolean(headerImageUrl || pageBackdropUrl)

  const paletteVars = {
    '--ys-bg':   activeBg,
    '--ys-text': palette.text,
    '--ys-ring': palette.ring,
  } as React.CSSProperties

  return (
    <main
      className={styles.page}
      style={paletteVars}
      data-page-backdrop={pageBackdropUrl ? 'true' : undefined}
      aria-label="Your recovery space"
    >

      {pageBackdropUrl && (
        <div
          className={styles.pageBackdrop}
          style={{ backgroundImage: `url(${pageBackdropUrl})` }}
          aria-hidden="true"
        />
      )}

      {headerImageUrl && !pageBackdropUrl && (
        <div
          className={styles.backdrop}
          style={{
            backgroundImage: `url(${headerImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          aria-hidden="true"
        />
      )}

      {!headerImageUrl && !pageBackdropUrl && (
        <div className={styles.backdrop} aria-hidden="true" />
      )}

      <div className={styles.controlRow}>
        <div className={styles.uploadControls}>
          <label
            htmlFor={headerUploadId}
            className={styles.cameraBtn}
            aria-label="Upload a header photo"
            title="Upload a header photo"
          >
            <CameraIcon />
            <input
              id={headerUploadId}
              ref={headerFileRef}
              type="file"
              accept="image/*"
              className={styles.hiddenInput}
              onChange={handleHeaderFile}
              aria-hidden="true"
              tabIndex={-1}
            />
          </label>

          <label
            htmlFor={pageUploadId}
            className={styles.cameraBtn}
            aria-label="Upload a background photo"
            title="Upload a background photo"
          >
            <LandscapeIcon />
            <input
              id={pageUploadId}
              ref={pageFileRef}
              type="file"
              accept="image/*"
              className={styles.hiddenInput}
              onChange={handlePageBackdropFile}
              aria-hidden="true"
              tabIndex={-1}
            />
          </label>

          {hasPhoto && (
            <button
              type="button"
              className={styles.removePhotoBtn}
              onClick={handleRemovePhoto}
            >
              Remove photo
            </button>
          )}
        </div>

        <div className={styles.paletteRow} role="group" aria-label="Choose background colour">
          {PALETTES.map(p => (
            <button
              key={p.id}
              type="button"
              className={styles.paletteCircle}
              style={{ background: p.swatch }}
              data-active={!useCustomColor && palette.id === p.id}
              onClick={() => handlePaletteSelect(p.id)}
              aria-label={p.label}
              aria-pressed={!useCustomColor && palette.id === p.id}
              title={p.label}
            >
              {!useCustomColor && palette.id === p.id && <TickIcon />}
            </button>
          ))}

          <label
            htmlFor={customColorId}
            className={styles.customColorPicker}
            data-active={useCustomColor}
            title="Choose your own colour"
            aria-label="Choose your own colour"
          >
            <input
              id={customColorId}
              type="color"
              className={styles.customColorInput}
              value={customColor}
              onChange={e => handleCustomColor(e.target.value)}
            />
            {useCustomColor && <span className={styles.customColorTick}><TickIcon /></span>}
          </label>
        </div>
      </div>

      <div className={styles.belowTop}>
        {pageBackdropUrl && softenBackground && (
          <div className={styles.contentSoften} aria-hidden="true" />
        )}

        {pageBackdropUrl && (
          <div className={styles.preferencesRow}>
            <label htmlFor={softenToggleId} className={styles.softenToggle}>
              <input
                id={softenToggleId}
                type="checkbox"
                className={styles.softenCheckbox}
                checked={softenBackground}
                onChange={e => handleSoftenToggle(e.target.checked)}
              />
              <span className={styles.softenLabel}>Soften background</span>
            </label>
          </div>
        )}

        <div className={styles.inner}>
          <header className={styles.header}>
            <h1 className={styles.heading}>Your Space</h1>
            <p className={styles.subheading}>
              Your notes, your resources, your thoughts and feelings
            </p>
          </header>

          <button type="button" className={styles.endSessionBtn} onClick={logout}>
            End session
          </button>

          <section className={styles.section}>
            <label htmlFor={textareaId} className={styles.visuallyHidden}>
              Write a note to yourself
            </label>
            <textarea
              id={textareaId}
              className={styles.noteArea}
              placeholder="Something that came up after the session..."
              value={note}
              onChange={handleNoteChange}
              rows={10}
              spellCheck
              aria-multiline="true"
            />
            <div className={styles.noteActions}>
              <button className={styles.actionBtn} disabled aria-disabled="true">
                Keep for Me
              </button>
              <button className={styles.actionBtn} disabled aria-disabled="true">
                Share
              </button>
            </div>
          </section>

          <section className={styles.helpfulSection} aria-labelledby="helpful-heading">
            <h2 id="helpful-heading" className={styles.helpfulHeading}>
              What Helps Me
            </h2>
            <p className={styles.helpfulPlaceholder}>
              Resources coming soon.
            </p>
          </section>
        </div>
      </div>
    </main>
  )
}
